/** Pure geometry: DOM order is never changed, and no camera frame is cropped. */
export const CAMERA_FALLBACK_RATIO = 16 / 9;
export const CAMERA_TILE_CHROME = 86; // tools + label + gaps, padding and border
export const CAMERA_MIN_WIDTH = 124;
export const CAMERA_MIN_MEDIA_HEIGHT = 56;
export const CAMERA_GAP = 8;
export const cameraRatio = (value) => value && Number.isFinite(value) && value > 0 ? value : CAMERA_FALLBACK_RATIO;
export function containedCameraSize(width, height, ratio) {
    const r = cameraRatio(ratio);
    const h = Math.max(0, Math.min(height, width / r));
    return { width: h * r, height: h };
}
/** Contiguous justified rows. Small calls search all feasible partitions;
 * large calls use bounded balanced candidates rather than exponential work. */
function partitions(count, columns) {
    if (count > 10) {
        return Array.from({ length: columns }, (_, i) => {
            const rows = Math.ceil(count / (i + 1));
            return Array.from({ length: rows }, (_, row) => Math.floor(count / rows) + (row < count % rows ? 1 : 0));
        });
    }
    const result = [];
    const visit = (left, rows) => {
        if (!left) {
            result.push(rows);
            return;
        }
        for (let n = 1; n <= Math.min(left, columns); n++)
            visit(left - n, [...rows, n]);
    };
    visit(count, []);
    return result;
}
export function layoutCameraGrid(width, height, inputRatios, focusedIndex = -1, tileChrome = CAMERA_TILE_CHROME) {
    const w = Math.max(1, Number.isFinite(width) ? width : 1);
    const h = Math.max(1, Number.isFinite(height) ? height : 1);
    const ratios = inputRatios.map(cameraRatio);
    if (!ratios.length)
        return { boxes: [], height: h, visibleArea: 0 };
    const minWidth = Math.min(CAMERA_MIN_WIDTH, w);
    const minHeight = tileChrome + CAMERA_MIN_MEDIA_HEIGHT;
    const columns = Math.max(1, Math.floor((w + CAMERA_GAP) / (minWidth + CAMERA_GAP)));
    if (focusedIndex >= 0 && focusedIndex < ratios.length && ratios.length > 1) {
        const baseline = layoutCameraGrid(w, h, ratios, -1, tileChrome).boxes[focusedIndex];
        const previousImage = containedCameraSize(Math.max(0, baseline.width - 14), baseline.height - tileChrome, ratios[focusedIndex]);
        // In constrained calls, enlargement may require scrolling the peers.
        // Grow the selected image rather than merely giving it wider letterboxes,
        // but keep its whole tile within the viewport whenever that is possible.
        const focusMinimum = Math.max(minHeight, Math.min(h, tileChrome + previousImage.height * 1.35));
        const others = ratios.filter((_, i) => i !== focusedIndex);
        const stripRows = Math.ceil(others.length / columns);
        const stripHeight = stripRows * minHeight + (stripRows - 1) * CAMERA_GAP;
        const fullHeight = Math.max(h, focusMinimum + CAMERA_GAP + stripHeight);
        const focusHeight = fullHeight - stripHeight - CAMERA_GAP;
        const bottom = layoutCameraGrid(w, stripHeight, others, -1, tileChrome);
        const candidates = [{
                focus: { x: 0, y: 0, width: w, height: focusHeight },
                others: bottom.boxes.map(box => ({ ...box, y: box.y + focusHeight + CAMERA_GAP })),
                height: fullHeight,
            }];
        // Portrait cameras can benefit more from a tall focus beside the peers.
        // Prefer in-viewport candidates; do not win by adding offscreen area.
        if (w >= CAMERA_MIN_WIDTH * 2 + CAMERA_GAP) {
            const sideWidth = Math.max(CAMERA_MIN_WIDTH, Math.min(w * .26, w - CAMERA_MIN_WIDTH - CAMERA_GAP));
            const side = layoutCameraGrid(sideWidth, h, others, -1, tileChrome);
            const focusWidth = w - sideWidth - CAMERA_GAP;
            candidates.push({
                focus: { x: 0, y: 0, width: focusWidth, height: Math.max(minHeight, h) },
                others: side.boxes.map(box => ({ ...box, x: box.x + focusWidth + CAMERA_GAP })),
                height: side.height,
            });
        }
        const fitting = candidates.filter(c => c.height <= h + .5);
        const candidateArea = (c) => {
            const s = containedCameraSize(c.focus.width - 14, c.focus.height - tileChrome, ratios[focusedIndex]);
            return s.width * s.height;
        };
        const chosen = (fitting.length ? fitting : candidates).reduce((a, b) => candidateArea(b) > candidateArea(a) + 1 ? b : a);
        let otherIndex = 0;
        const boxes = ratios.map((_, i) => i === focusedIndex ? chosen.focus : chosen.others[otherIndex++]);
        return { boxes, height: chosen.height, visibleArea: boxes.reduce((area, box, i) => {
                const s = containedCameraSize(Math.max(0, box.width - 14), box.height - tileChrome, ratios[i]);
                return area + s.width * s.height;
            }, 0) };
    }
    let best;
    for (const rows of partitions(ratios.length, Math.min(columns, ratios.length))) {
        let index = 0;
        const rowData = rows.map(count => {
            const r = ratios.slice(index, index += count);
            const available = w - CAMERA_GAP * (count - 1);
            // The media plane excludes the tile padding/border. Tiny allocations
            // still reserve real space for tools instead of stacking over video.
            let low = 0, high = available / Math.min(...r);
            for (let step = 0; step < 28; step++) {
                const mid = (low + high) / 2;
                if (r.reduce((sum, ratio) => sum + Math.max(minWidth, ratio * mid + 14), 0) <= available)
                    low = mid;
                else
                    high = mid;
            }
            return { r, available, natural: Math.max(CAMERA_MIN_MEDIA_HEIGHT, low) };
        });
        const minimum = rows.length * minHeight + CAMERA_GAP * (rows.length - 1);
        const fullHeight = Math.max(h, minimum);
        const mediaBudget = fullHeight - rows.length * tileChrome - CAMERA_GAP * (rows.length - 1);
        // Water-fill with a floor, so a large portrait row cannot starve another.
        let low = 0, high = Math.max(1, mediaBudget / Math.min(...rowData.map(row => row.natural)));
        for (let step = 0; step < 28; step++) {
            const mid = (low + high) / 2;
            if (rowData.reduce((sum, row) => sum + Math.max(CAMERA_MIN_MEDIA_HEIGHT, row.natural * mid), 0) <= mediaBudget)
                low = mid;
            else
                high = mid;
        }
        const boxes = [];
        let y = 0, area = 0, minEdge = Infinity, maxEdge = 0;
        for (const row of rowData) {
            const mediaHeight = Math.max(CAMERA_MIN_MEDIA_HEIGHT, row.natural * low);
            const weights = row.r.map(r => Math.max(0, r * Math.min(mediaHeight, row.natural) + 14 - minWidth));
            const totalWeight = weights.reduce((sum, value) => sum + value, 0);
            const extraWidth = Math.max(0, row.available - row.r.length * minWidth);
            const ratioSum = row.r.reduce((sum, ratio) => sum + ratio, 0);
            let x = 0;
            row.r.forEach((ratio, i) => {
                // Never normalize a wide camera's weight at the expense of a peer's
                // control-space floor. Only distribute the width above that floor.
                const tileWidth = minWidth + extraWidth *
                    (totalWeight > .001 ? weights[i] / totalWeight : ratio / ratioSum);
                const tileHeight = mediaHeight + tileChrome;
                const image = containedCameraSize(Math.max(0, tileWidth - 14), mediaHeight, ratio);
                const edge = Math.min(image.width, image.height);
                area += image.width * image.height;
                minEdge = Math.min(minEdge, edge);
                maxEdge = Math.max(maxEdge, edge);
                boxes.push({ x, y, width: tileWidth, height: tileHeight });
                x += tileWidth + CAMERA_GAP;
            });
            y += mediaHeight + tileChrome + CAMERA_GAP;
        }
        // Optimize actual contained pixels, with a substantial fairness penalty
        // for slivers. Overflow is a last resort for insufficient control space.
        const score = area * (.45 + .55 * minEdge / Math.max(1, maxEdge));
        const overflow = minimum > h + .5;
        if (!best || (best.overflow && !overflow) ||
            (best.overflow && overflow && fullHeight < best.height - .5) ||
            (best.overflow === overflow && (!overflow || Math.abs(fullHeight - best.height) <= .5) && score > best.score + 1)) {
            best = { boxes, height: fullHeight, visibleArea: area, score, overflow };
        }
    }
    return best;
}
